import React, { useRef, useState, useEffect } from "react";

/*
  SearchWidget
  -------------
  Replaces the legacy inline search bar for filtering questions.
  Features:
    - Debounced input (100ms) calling onDebouncedSearch(value)
    - Reset link (calls onReset)
    - Tooltip (hover/focus toggle) explaining search behavior
  Props:
    value: string
    onChange: (value:string)=>void   // immediate input state
    onDebouncedSearch: (value:string)=>void // debounced fire
    onReset: ()=>void
*/

const SearchWidget = ({ value, onChange, onDebouncedSearch, onReset }) => {
  const timerRef = useRef(null);
  const [showTip, setShowTip] = useState(false);
  const tooltipId = "search-help-tip";

  // Fire debounced search when value changes (manual debounce control)
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onDebouncedSearch && onDebouncedSearch(value);
    }, 100);
    return () => clearTimeout(timerRef.current);
  }, [value, onDebouncedSearch]);

  function handleReset() {
    if (timerRef.current) clearTimeout(timerRef.current);
    onReset && onReset();
    onDebouncedSearch && onDebouncedSearch("");
  }

  function toggleTip(next) {
    setShowTip(next);
  }

  function handleKey(e) {
    if (e.key === "Escape") setShowTip(false);
    if (e.key === "Enter" || e.key === " ") setShowTip((s) => !s);
  }

  return (
    <div
      id="topics-search-widget"
      aria-label="question search"
      className="mt-3 sidebar"
      style={{
        border: "1px solid grey",
        borderRadius: "15px",
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between mb-2"
        style={{ lineHeight: 1.2 }}
      >
        <div className="d-flex align-items-center search-widget-container" style={{ gap: 6 }}>
          <span aria-hidden="true" style={{ fontSize: "0.85rem" }}>
            <span className="cdc-icon-search" />
          </span>
          <span className="search-widget-text">Search</span>
          <div
            className="search-tooltip-wrapper"
            onMouseEnter={() => toggleTip(true)}
            onMouseLeave={() => toggleTip(false)}
          >
            <button
              type="button"
              aria-label="Search help"
              aria-describedby={showTip ? tooltipId : undefined}
              className="cdc-icon-question-circle-solid search-tooltip btn btn-link p-0"
              onFocus={() => toggleTip(true)}
              onBlur={() => toggleTip(false)}
              onKeyDown={handleKey}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 'inherit' }}
            />
            {showTip && (
              <div
                id={tooltipId}
                role="tooltip"
                className="search-help-tooltip"
              >
                Search pulls results from matches to topics, subtopics, questions, subquestions, and additional keywords.
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-link p-0 underline"
          onClick={handleReset}
        >
          Reset
        </button>
      </div>
      <input
        type="text"
        value={value}
        name="searchQuestion"
        className="form-control"
        onChange={(e) => onChange(e.target.value)}
        aria-label="Search questions"
        placeholder="Enter keyword(s)"
      />
    </div>
  );
};

export default SearchWidget;
